import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import {
  AlertTriangle,
  Cable,
  Check,
  Cpu,
  Eye,
  EyeOff,
  Globe,
  Key,
  Layers,
  Lock,
  MapPin,
  Power,
  RefreshCw,
  Router,
  ShieldAlert,
  Sliders,
  Wifi,
  X,
  Zap,
} from "lucide-react";
import { SmartOLTTopology } from "../../services/smartolt.service.ts";

export type ModalType =
  | "wifi"
  | "speed"
  | "location"
  | "ont_swap"
  | "wan"
  | "web_password"
  | "lan_port"
  | "confirm_destructive"
  | null;

export interface ModalContextData {
  onuExternalId: string;
  onuName?: string;
  currentSn?: string;
  currentWifi?: {
    port?: string;
    ssid?: string;
    password?: string;
    auth_mode?: string;
  };
  currentSpeed?: {
    upload_profile?: string;
    download_profile?: string;
  };
  currentLocation?: {
    zone?: string;
    odb?: string;
    odb_port?: string;
    address?: string;
    contact?: string;
    latitude?: number | string;
    longitude?: number | string;
  };
  currentWan?: {
    mode?: string;
    username?: string;
    ip?: string;
    mask?: string;
    gateway?: string;
    dns1?: string;
  };
  destructiveAction?: {
    key: string;
    title: string;
    description: string;
    confirmKeyword: string;
    buttonLabel: string;
  };
}

interface SmartOLTActionModalsProps {
  activeModal: ModalType;
  onClose: () => void;
  onSubmit: (operationKey: string, payload: Record<string, any>) => Promise<boolean | void>;
  context: ModalContextData | null;
  topology?: SmartOLTTopology | null;
  busy?: boolean;
}

export const SmartOLTActionModals: React.FC<SmartOLTActionModalsProps> = ({
  activeModal,
  onClose,
  onSubmit,
  context,
  topology,
  busy = false,
}) => {
  useEffect(() => {
    if (!activeModal) return;
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !busy) {
        onClose();
      }
    };
    window.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = originalOverflow;
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [activeModal, busy, onClose]);

  if (!activeModal || !context) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[9999] w-screen h-screen min-h-dvh flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-xs transition-opacity animate-in fade-in duration-150"
      onClick={(e) => {
        if (e.target === e.currentTarget && !busy) {
          onClose();
        }
      }}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="relative w-full max-w-lg bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden flex flex-col max-h-[90vh] animate-in zoom-in-95 duration-150"
        onClick={(e) => e.stopPropagation()}
      >
        {activeModal === "wifi" && (
          <WifiModalInner context={context} onClose={onClose} onSubmit={onSubmit} busy={busy} />
        )}
        {activeModal === "speed" && (
          <SpeedModalInner
            context={context}
            topology={topology}
            onClose={onClose}
            onSubmit={onSubmit}
            busy={busy}
          />
        )}
        {activeModal === "location" && (
          <LocationModalInner
            context={context}
            topology={topology}
            onClose={onClose}
            onSubmit={onSubmit}
            busy={busy}
          />
        )}
        {activeModal === "ont_swap" && (
          <OntSwapModalInner context={context} onClose={onClose} onSubmit={onSubmit} busy={busy} />
        )}
        {activeModal === "wan" && (
          <WanModalInner context={context} onClose={onClose} onSubmit={onSubmit} busy={busy} />
        )}
        {activeModal === "web_password" && (
          <WebPasswordModalInner context={context} onClose={onClose} onSubmit={onSubmit} busy={busy} />
        )}
        {activeModal === "lan_port" && (
          <LanPortModalInner
            context={context}
            topology={topology}
            onClose={onClose}
            onSubmit={onSubmit}
            busy={busy}
          />
        )}
        {activeModal === "confirm_destructive" && (
          <DestructiveConfirmModalInner
            context={context}
            onClose={onClose}
            onSubmit={onSubmit}
            busy={busy}
          />
        )}
      </div>
    </div>,
    document.body
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// 1. Wi-Fi Configuration Modal
// ─────────────────────────────────────────────────────────────────────────────
function WifiModalInner({
  context,
  onClose,
  onSubmit,
  busy,
}: {
  context: ModalContextData;
  onClose: () => void;
  onSubmit: (op: string, payload: Record<string, any>) => Promise<any>;
  busy: boolean;
}) {
  const [wifiPort, setWifiPort] = useState(context.currentWifi?.port || "1");
  const [ssid, setSsid] = useState(context.currentWifi?.ssid || "");
  const [password, setPassword] = useState(context.currentWifi?.password || "");
  const [authMode, setAuthMode] = useState(context.currentWifi?.auth_mode || "wpa2-psk");
  const [dhcp, setDhcp] = useState("enabled");
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit("set_onu_wifi_port_mode_lan_by_external_id", {
      form_data: {
        wifi_port: wifiPort,
        ssid: ssid.trim(),
        password: password.trim(),
        authentication_mode: authMode,
        dhcp: dhcp,
      },
    });
  };

  return (
    <form onSubmit={handleSubmit} className="flex flex-col h-full">
      <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 bg-slate-50">
        <div className="flex items-center gap-2.5">
          <div className="p-2 rounded-xl bg-blue-50 text-blue-600 border border-blue-200">
            <Wifi className="h-5 w-5" />
          </div>
          <div>
            <h3 className="text-sm font-bold text-slate-900">Configure Wi-Fi Network</h3>
            <p className="text-xs text-slate-500">Update SSID, security passphrase, and radio profile</p>
          </div>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="p-1 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      <div className="p-6 space-y-4 overflow-y-auto">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1">Wi-Fi Port / Band</label>
            <select
              value={wifiPort}
              onChange={(e) => setWifiPort(e.target.value)}
              className="w-full h-9 border border-slate-200 rounded-xl px-3 text-xs font-medium text-slate-800 bg-white shadow-xs focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
            >
              <option value="1">Port 1 (Primary / 2.4 GHz)</option>
              <option value="2">Port 2 (Secondary / 5 GHz)</option>
              <option value="3">Port 3 (Guest / SSID 3)</option>
              <option value="4">Port 4 (SSID 4)</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1">Security Mode</label>
            <select
              value={authMode}
              onChange={(e) => setAuthMode(e.target.value)}
              className="w-full h-9 border border-slate-200 rounded-xl px-3 text-xs font-medium text-slate-800 bg-white shadow-xs focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
            >
              <option value="wpa2-psk">WPA2-PSK (Recommended)</option>
              <option value="wpa-wpa2-psk">WPA / WPA2 Mixed</option>
              <option value="wpa-psk">WPA-PSK</option>
              <option value="open">Open (No Password)</option>
            </select>
          </div>
        </div>

        <div>
          <label className="block text-xs font-semibold text-slate-700 mb-1">Wi-Fi SSID Name</label>
          <input
            type="text"
            required
            value={ssid}
            onChange={(e) => setSsid(e.target.value)}
            placeholder="e.g. Home_Network_5G"
            className="w-full h-9 border border-slate-200 rounded-xl px-3 text-xs font-medium text-slate-800 bg-white placeholder-slate-400 shadow-xs focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
          />
        </div>

        {authMode !== "open" && (
          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1">Passphrase / Key (WPA2)</label>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                required
                minLength={8}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Minimum 8 characters"
                className="w-full h-9 border border-slate-200 rounded-xl pl-3 pr-9 text-xs font-mono text-slate-800 bg-white placeholder-slate-400 shadow-xs focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute inset-y-0 right-0 pr-3 flex items-center text-slate-400 hover:text-slate-600"
              >
                {showPassword ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
              </button>
            </div>
          </div>
        )}

        <div>
          <label className="block text-xs font-semibold text-slate-700 mb-1">DHCP Server On Wi-Fi</label>
          <select
            value={dhcp}
            onChange={(e) => setDhcp(e.target.value)}
            className="w-full h-9 border border-slate-200 rounded-xl px-3 text-xs font-medium text-slate-800 bg-white shadow-xs focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
          >
            <option value="enabled">Enabled (Router provides IP)</option>
            <option value="disabled">Disabled (Bridge / External DHCP)</option>
          </select>
        </div>
      </div>

      <div className="px-6 py-3.5 border-t border-slate-200 bg-slate-50 flex justify-end gap-2">
        <button
          type="button"
          onClick={onClose}
          disabled={busy}
          className="px-4 py-2 text-xs font-medium text-slate-700 hover:bg-slate-100 border border-slate-200 bg-white rounded-xl shadow-xs transition-all"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={busy || !ssid.trim() || (authMode !== "open" && password.length < 8)}
          className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold rounded-xl shadow-xs transition-all disabled:opacity-50 flex items-center gap-1.5"
        >
          {busy && <RefreshCw className="h-3.5 w-3.5 animate-spin" />}
          Apply Wi-Fi Settings
        </button>
      </div>
    </form>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// 2. Speed Profile Modal
// ─────────────────────────────────────────────────────────────────────────────
function SpeedModalInner({
  context,
  topology,
  onClose,
  onSubmit,
  busy,
}: {
  context: ModalContextData;
  topology?: SmartOLTTopology | null;
  onClose: () => void;
  onSubmit: (op: string, payload: Record<string, any>) => Promise<any>;
  busy: boolean;
}) {
  const [upProfile, setUpProfile] = useState(context.currentSpeed?.upload_profile || "");
  const [downProfile, setDownProfile] = useState(context.currentSpeed?.download_profile || "");

  const profiles = topology?.speed_profiles || [];

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit("update_onu_speed_profiles_by_external_id", {
      form_data: {
        upload_speed_profile_name: upProfile,
        download_speed_profile_name: downProfile,
      },
    });
  };

  return (
    <form onSubmit={handleSubmit} className="flex flex-col h-full">
      <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 bg-slate-50">
        <div className="flex items-center gap-2.5">
          <div className="p-2 rounded-xl bg-amber-50 text-amber-600 border border-amber-200">
            <Zap className="h-5 w-5" />
          </div>
          <div>
            <h3 className="text-sm font-bold text-slate-900">Speed Profiles</h3>
            <p className="text-xs text-slate-500">Apply upload and download speed profiles on customer ONU</p>
          </div>
        </div>
        <button type="button" onClick={onClose} className="p-1 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors">
          <X className="h-4 w-4" />
        </button>
      </div>

      <div className="p-6 space-y-4">
        {context.currentSpeed?.upload_profile && (
          <div className="p-3 rounded-xl bg-slate-50 border border-slate-200 text-xs">
            <span className="text-slate-500 block text-[10px] uppercase font-bold mb-0.5">Current Provisioned Profiles</span>
            <span className="font-mono text-amber-700 font-semibold">
              ↑ {context.currentSpeed.upload_profile} / ↓ {context.currentSpeed.download_profile}
            </span>
          </div>
        )}

        <div>
          <label className="block text-xs font-semibold text-slate-700 mb-1">Upload Speed Profile</label>
          <select
            required
            value={upProfile}
            onChange={(e) => setUpProfile(e.target.value)}
            className="w-full h-9 border border-slate-200 rounded-xl px-3 text-xs font-medium text-slate-800 bg-white shadow-xs focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500"
          >
            <option value="" className="text-slate-400">Select Upload Profile...</option>
            {profiles.map((p, idx) => (
              <option key={p.profile_id || p.id || p.name || idx} value={p.name}>
                {p.name}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-xs font-semibold text-slate-700 mb-1">Download Speed Profile</label>
          <select
            required
            value={downProfile}
            onChange={(e) => setDownProfile(e.target.value)}
            className="w-full h-9 border border-slate-200 rounded-xl px-3 text-xs font-medium text-slate-800 bg-white shadow-xs focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500"
          >
            <option value="" className="text-slate-400">Select Download Profile...</option>
            {profiles.map((p, idx) => (
              <option key={p.profile_id || p.id || p.name || idx} value={p.name}>
                {p.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="px-6 py-3.5 border-t border-slate-200 bg-slate-50 flex justify-end gap-2">
        <button type="button" onClick={onClose} disabled={busy} className="px-4 py-2 text-xs font-medium text-slate-700 rounded-xl hover:bg-slate-100 border border-slate-200 bg-white shadow-xs transition-all">
          Cancel
        </button>
        <button
          type="submit"
          disabled={busy || !upProfile || !downProfile}
          className="px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white text-xs font-bold rounded-xl shadow-xs transition-all disabled:opacity-50 flex items-center gap-1.5"
        >
          {busy && <RefreshCw className="h-3.5 w-3.5 animate-spin" />}
          Update Speed Profiles
        </button>
      </div>
    </form>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// 3. Location & Splitter (ODB) Relocation Modal
// ─────────────────────────────────────────────────────────────────────────────
function LocationModalInner({
  context,
  topology,
  onClose,
  onSubmit,
  busy,
}: {
  context: ModalContextData;
  topology?: SmartOLTTopology | null;
  onClose: () => void;
  onSubmit: (op: string, payload: Record<string, any>) => Promise<any>;
  busy: boolean;
}) {
  const [zone, setZone] = useState(context.currentLocation?.zone || "");
  const [odb, setOdb] = useState(context.currentLocation?.odb || "");
  const [odbPort, setOdbPort] = useState(context.currentLocation?.odb_port || "1");
  const [address, setAddress] = useState(context.currentLocation?.address || "");
  const [contact, setContact] = useState(context.currentLocation?.contact || "");
  const [lat, setLat] = useState<string>(String(context.currentLocation?.latitude || ""));
  const [lng, setLng] = useState<string>(String(context.currentLocation?.longitude || ""));

  const zones = topology?.zones || [];
  const odbs = topology?.odbs || [];
  const selectedZone = zones.find((z) => String(z.zone_id) === String(zone) || String(z.name || "") === String(zone));
  const selectedZoneId = selectedZone?.zone_id != null ? String(selectedZone.zone_id) : String(zone || "");

  const filteredOdbs = selectedZoneId ? odbs.filter((o) => !o.zone_id || String(o.zone_id) === selectedZoneId) : odbs;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit("update_onu_location_details_by_external_id", {
      form_data: {
        zone: zone,
        odb: odb,
        odb_port: odbPort,
        name: context.onuName || "",
        address_or_comment: address.trim(),
        contact: contact.trim(),
        latitude: lat ? parseFloat(lat) : "",
        longitude: lng ? parseFloat(lng) : "",
      },
    });
  };

  return (
    <form onSubmit={handleSubmit} className="flex flex-col h-full">
      <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 bg-slate-50">
        <div className="flex items-center gap-2.5">
          <div className="p-2 rounded-xl bg-emerald-50 text-emerald-600 border border-emerald-200">
            <MapPin className="h-5 w-5" />
          </div>
          <div>
            <h3 className="text-sm font-bold text-slate-900">Zone & ODB Details</h3>
            <p className="text-xs text-slate-500">Update Zone, ODB splitter box, port, address, and GPS</p>
          </div>
        </div>
        <button type="button" onClick={onClose} className="p-1 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors">
          <X className="h-4 w-4" />
        </button>
      </div>

      <div className="p-6 space-y-3.5 overflow-y-auto max-h-[60vh]">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1">Zone</label>
            <select
              value={zone}
              onChange={(e) => {
                setZone(e.target.value);
                setOdb("");
              }}
              className="w-full h-9 border border-slate-200 rounded-xl px-3 text-xs font-medium text-slate-800 bg-white shadow-xs focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
            >
              <option value="" className="text-slate-400">Select Zone...</option>
              {zones.map((z) => (
                <option key={z.zone_id} value={z.name || z.zone_id}>
                  {z.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1">Splitter Box (ODB)</label>
            <select
              required
              value={odb}
              onChange={(e) => setOdb(e.target.value)}
              className="w-full h-9 border border-slate-200 rounded-xl px-3 text-xs font-medium text-slate-800 bg-white shadow-xs focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
            >
              <option value="" className="text-slate-400">Select ODB...</option>
              {filteredOdbs.map((o) => (
                <option key={o.odb_id} value={o.name || o.odb_id}>
                  {o.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div>
          <label className="block text-xs font-semibold text-slate-700 mb-1">ODB Splitter Port</label>
          <input
            type="number"
            min={1}
            max={64}
            value={odbPort}
            onChange={(e) => setOdbPort(e.target.value)}
            className="w-full h-9 border border-slate-200 rounded-xl px-3 text-xs font-medium text-slate-800 bg-white shadow-xs focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
          />
        </div>

        <div>
          <label className="block text-xs font-semibold text-slate-700 mb-1">Installation Address / Comment</label>
          <input
            type="text"
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            placeholder="e.g. Street 4, House 12, Floor 2"
            className="w-full h-9 border border-slate-200 rounded-xl px-3 text-xs font-medium text-slate-800 bg-white placeholder-slate-400 shadow-xs focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1">Latitude</label>
            <input
              type="text"
              value={lat}
              onChange={(e) => setLat(e.target.value)}
              placeholder="e.g. 24.8607"
              className="w-full h-9 border border-slate-200 rounded-xl px-3 text-xs font-mono text-slate-800 bg-white placeholder-slate-400 shadow-xs focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1">Longitude</label>
            <input
              type="text"
              value={lng}
              onChange={(e) => setLng(e.target.value)}
              placeholder="e.g. 67.0011"
              className="w-full h-9 border border-slate-200 rounded-xl px-3 text-xs font-mono text-slate-800 bg-white placeholder-slate-400 shadow-xs focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
            />
          </div>
        </div>
      </div>

      <div className="px-6 py-3.5 border-t border-slate-200 bg-slate-50 flex justify-end gap-2">
        <button type="button" onClick={onClose} disabled={busy} className="px-4 py-2 text-xs font-medium text-slate-700 rounded-xl hover:bg-slate-100 border border-slate-200 bg-white shadow-xs transition-all">
          Cancel
        </button>
        <button
          type="submit"
          disabled={busy || !odb}
          className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold rounded-xl shadow-xs transition-all disabled:opacity-50 flex items-center gap-1.5"
        >
          {busy && <RefreshCw className="h-3.5 w-3.5 animate-spin" />}
          Update Location Details
        </button>
      </div>
    </form>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// 4. Hardware ONT Swap Modal (RMA Replacement)
// ─────────────────────────────────────────────────────────────────────────────
function OntSwapModalInner({
  context,
  onClose,
  onSubmit,
  busy,
}: {
  context: ModalContextData;
  onClose: () => void;
  onSubmit: (op: string, payload: Record<string, any>) => Promise<any>;
  busy: boolean;
}) {
  const [newSn, setNewSn] = useState("");
  const [confirmed, setConfirmed] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit("update_onu_sn_mac_by_external_id", {
      form_data: {
        new_sn: newSn.trim().toUpperCase(),
      },
    });
  };

  return (
    <form onSubmit={handleSubmit} className="flex flex-col h-full">
      <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 bg-slate-50">
        <div className="flex items-center gap-2.5">
          <div className="p-2 rounded-xl bg-purple-50 text-purple-600 border border-purple-200">
            <RefreshCw className="h-5 w-5" />
          </div>
          <div>
            <h3 className="text-sm font-bold text-slate-900">Swap ONU (Change SN)</h3>
            <p className="text-xs text-slate-500">Replace physical ONU serial number without resetting profile</p>
          </div>
        </div>
        <button type="button" onClick={onClose} className="p-1 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors">
          <X className="h-4 w-4" />
        </button>
      </div>

      <div className="p-6 space-y-4">
        <div className="p-3.5 rounded-xl bg-purple-50 border border-purple-200 text-xs text-purple-900">
          <p className="font-semibold mb-1">How ONT Hardware Replacement Works:</p>
          <p className="text-purple-800 leading-relaxed text-[11px]">
            Updating the ONT serial number seamlessly shifts optical authorization to the new physical device while preserving customer billing IDs, historical graphs, VLAN tags, and Wi-Fi credentials.
          </p>
        </div>

        <div className="p-3 rounded-xl bg-slate-50 border border-slate-200">
          <span className="text-slate-500 block text-[10px] uppercase font-bold">Current Active Serial Number</span>
          <strong className="font-mono text-purple-700 text-sm">{context.currentSn || "—"}</strong>
        </div>

        <div>
          <label className="block text-xs font-semibold text-slate-700 mb-1">New ONT Serial Number (SN)</label>
          <input
            type="text"
            required
            minLength={8}
            maxLength={20}
            value={newSn}
            onChange={(e) => setNewSn(e.target.value.toUpperCase())}
            placeholder="e.g. HWTC12345678 or ZTEGC1234567"
            className="w-full h-10 border border-slate-200 rounded-xl px-3 text-xs font-mono uppercase text-slate-900 font-bold bg-white placeholder-slate-400 shadow-xs focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500"
          />
        </div>

        <label className="flex items-start gap-2 pt-2 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={confirmed}
            onChange={(e) => setConfirmed(e.target.checked)}
            className="mt-0.5 h-4 w-4 rounded border-slate-300 bg-white text-purple-600 focus:ring-purple-500/20"
          />
          <span className="text-xs text-slate-700">
            I confirm that the new ONT is physically connected to the fiber line and powered on.
          </span>
        </label>
      </div>

      <div className="px-6 py-3.5 border-t border-slate-200 bg-slate-50 flex justify-end gap-2">
        <button type="button" onClick={onClose} disabled={busy} className="px-4 py-2 text-xs font-medium text-slate-700 rounded-xl hover:bg-slate-100 border border-slate-200 bg-white shadow-xs transition-all">
          Cancel
        </button>
        <button
          type="submit"
          disabled={busy || !newSn.trim() || !confirmed}
          className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white text-xs font-bold rounded-xl shadow-xs transition-all disabled:opacity-50 flex items-center gap-1.5"
        >
          {busy && <RefreshCw className="h-3.5 w-3.5 animate-spin" />}
          Execute Hardware Swap
        </button>
      </div>
    </form>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// 5. WAN Configuration Modal (PPPoE / Static IP / DHCP)
// ─────────────────────────────────────────────────────────────────────────────
function WanModalInner({
  context,
  onClose,
  onSubmit,
  busy,
}: {
  context: ModalContextData;
  onClose: () => void;
  onSubmit: (op: string, payload: Record<string, any>) => Promise<any>;
  busy: boolean;
}) {
  const [wanMode, setWanMode] = useState<"pppoe" | "static" | "dhcp">("pppoe");
  const [username, setUsername] = useState(context.currentWan?.username || "");
  const [password, setPassword] = useState("");
  const [ipAddress, setIpAddress] = useState(context.currentWan?.ip || "");
  const [subnetMask, setSubnetMask] = useState(context.currentWan?.mask || "255.255.255.0");
  const [gateway, setGateway] = useState(context.currentWan?.gateway || "");
  const [dns1, setDns1] = useState(context.currentWan?.dns1 || "8.8.8.8");
  const [dns2, setDns2] = useState("8.8.4.4");
  const [ipProtocol, setIpProtocol] = useState("ipv4");

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (wanMode === "pppoe") {
      onSubmit("set_onu_wan_mode_pppoe_by_external_id", {
        form_data: {
          username: username.trim(),
          password: password.trim(),
          configuration_method: "omci",
          ip_protocol: ipProtocol,
        },
      });
    } else if (wanMode === "static") {
      onSubmit("set_onu_wan_mode_static_ip_by_external_id", {
        form_data: {
          ipv4_address: ipAddress.trim(),
          subnet_mask: subnetMask.trim(),
          gateway: gateway.trim(),
          dns1: dns1.trim(),
          dns2: dns2.trim(),
          configuration_method: "omci",
          ip_protocol: "ipv4",
        },
      });
    } else {
      onSubmit("set_onu_wan_mode_dhcp_by_external_id", {
        form_data: {
          configuration_method: "omci",
          ip_protocol: ipProtocol,
        },
      });
    }
  };

  return (
    <form onSubmit={handleSubmit} className="flex flex-col h-full">
      <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 bg-slate-50">
        <div className="flex items-center gap-2.5">
          <div className="p-2 rounded-xl bg-cyan-50 text-cyan-600 border border-cyan-200">
            <Globe className="h-5 w-5" />
          </div>
          <div>
            <h3 className="text-sm font-bold text-slate-900">Set WAN Mode</h3>
            <p className="text-xs text-slate-500">Set PPPoE, Static IP, or DHCP routing on ONU</p>
          </div>
        </div>
        <button type="button" onClick={onClose} className="p-1 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors">
          <X className="h-4 w-4" />
        </button>
      </div>

      <div className="p-6 space-y-4 overflow-y-auto max-h-[60vh]">
        <div className="flex gap-2 p-1 bg-slate-100 border border-slate-200 rounded-xl">
          <button
            type="button"
            onClick={() => setWanMode("pppoe")}
            className={`flex-1 py-1.5 rounded-lg text-xs font-bold transition-all ${
              wanMode === "pppoe" ? "bg-white text-cyan-700 shadow-xs" : "text-slate-600 hover:text-slate-900"
            }`}
          >
            PPPoE Client
          </button>
          <button
            type="button"
            onClick={() => setWanMode("static")}
            className={`flex-1 py-1.5 rounded-lg text-xs font-bold transition-all ${
              wanMode === "static" ? "bg-white text-cyan-700 shadow-xs" : "text-slate-600 hover:text-slate-900"
            }`}
          >
            Static IP
          </button>
          <button
            type="button"
            onClick={() => setWanMode("dhcp")}
            className={`flex-1 py-1.5 rounded-lg text-xs font-bold transition-all ${
              wanMode === "dhcp" ? "bg-white text-cyan-700 shadow-xs" : "text-slate-600 hover:text-slate-900"
            }`}
          >
            DHCP Client
          </button>
        </div>

        {wanMode === "pppoe" && (
          <div className="space-y-3">
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">PPPoE Username</label>
              <input
                type="text"
                required
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="customer_pppoe_login"
                className="w-full h-9 border border-slate-200 rounded-xl px-3 text-xs font-medium text-slate-800 bg-white placeholder-slate-400 shadow-xs focus:ring-2 focus:ring-cyan-500/20 focus:border-cyan-500"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">PPPoE Password</label>
              <input
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="w-full h-9 border border-slate-200 rounded-xl px-3 text-xs font-medium text-slate-800 bg-white placeholder-slate-400 shadow-xs focus:ring-2 focus:ring-cyan-500/20 focus:border-cyan-500"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">IP Protocol</label>
              <select
                value={ipProtocol}
                onChange={(e) => setIpProtocol(e.target.value)}
                className="w-full h-9 border border-slate-200 rounded-xl px-3 text-xs font-medium text-slate-800 bg-white shadow-xs focus:ring-2 focus:ring-cyan-500/20 focus:border-cyan-500"
              >
                <option value="ipv4">IPv4 Only</option>
                <option value="ipv4_ipv6">Dual Stack (IPv4 / IPv6)</option>
              </select>
            </div>
          </div>
        )}

        {wanMode === "static" && (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">IPv4 Address</label>
                <input
                  type="text"
                  required
                  value={ipAddress}
                  onChange={(e) => setIpAddress(e.target.value)}
                  placeholder="e.g. 192.168.10.50"
                  className="w-full h-9 border border-slate-200 rounded-xl px-3 text-xs font-mono text-slate-800 bg-white placeholder-slate-400 shadow-xs focus:ring-2 focus:ring-cyan-500/20 focus:border-cyan-500"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">Subnet Mask</label>
                <input
                  type="text"
                  required
                  value={subnetMask}
                  onChange={(e) => setSubnetMask(e.target.value)}
                  placeholder="255.255.255.0"
                  className="w-full h-9 border border-slate-200 rounded-xl px-3 text-xs font-mono text-slate-800 bg-white placeholder-slate-400 shadow-xs focus:ring-2 focus:ring-cyan-500/20 focus:border-cyan-500"
                />
              </div>
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">Default Gateway</label>
              <input
                type="text"
                required
                value={gateway}
                onChange={(e) => setGateway(e.target.value)}
                placeholder="e.g. 192.168.10.1"
                className="w-full h-9 border border-slate-200 rounded-xl px-3 text-xs font-mono text-slate-800 bg-white placeholder-slate-400 shadow-xs focus:ring-2 focus:ring-cyan-500/20 focus:border-cyan-500"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">DNS 1</label>
                <input
                  type="text"
                  value={dns1}
                  onChange={(e) => setDns1(e.target.value)}
                  className="w-full h-9 border border-slate-200 rounded-xl px-3 text-xs font-mono text-slate-800 bg-white placeholder-slate-400 shadow-xs focus:ring-2 focus:ring-cyan-500/20 focus:border-cyan-500"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">DNS 2</label>
                <input
                  type="text"
                  value={dns2}
                  onChange={(e) => setDns2(e.target.value)}
                  className="w-full h-9 border border-slate-200 rounded-xl px-3 text-xs font-mono text-slate-800 bg-white placeholder-slate-400 shadow-xs focus:ring-2 focus:ring-cyan-500/20 focus:border-cyan-500"
                />
              </div>
            </div>
          </div>
        )}

        {wanMode === "dhcp" && (
          <div className="p-4 rounded-xl bg-slate-50 border border-slate-200 text-xs text-slate-700">
            Router WAN interface will request an IP address dynamically from the upstream ISP BRAS/DHCP server via OMCI.
          </div>
        )}
      </div>

      <div className="px-6 py-3.5 border-t border-slate-200 bg-slate-50 flex justify-end gap-2">
        <button type="button" onClick={onClose} disabled={busy} className="px-4 py-2 text-xs font-medium text-slate-700 rounded-xl hover:bg-slate-100 border border-slate-200 bg-white shadow-xs transition-all">
          Cancel
        </button>
        <button
          type="submit"
          disabled={busy || (wanMode === "pppoe" && (!username || !password)) || (wanMode === "static" && !ipAddress)}
          className="px-4 py-2 bg-cyan-600 hover:bg-cyan-700 text-white text-xs font-bold rounded-xl shadow-xs transition-all disabled:opacity-50 flex items-center gap-1.5"
        >
          {busy && <RefreshCw className="h-3.5 w-3.5 animate-spin" />}
          Apply WAN Configuration
        </button>
      </div>
    </form>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// 6. Router Web Admin Password Modal
// ─────────────────────────────────────────────────────────────────────────────
function WebPasswordModalInner({
  context,
  onClose,
  onSubmit,
  busy,
}: {
  context: ModalContextData;
  onClose: () => void;
  onSubmit: (op: string, payload: Record<string, any>) => Promise<any>;
  busy: boolean;
}) {
  const [webUser, setWebUser] = useState("admin");
  const [webPass, setWebPass] = useState("");
  const [showPass, setShowPass] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit("change_onu_web_user_password_by_external_id", {
      form_data: {
        web_user: webUser.trim(),
        web_pass: webPass.trim(),
      },
    });
  };

  return (
    <form onSubmit={handleSubmit} className="flex flex-col h-full">
      <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 bg-slate-50">
        <div className="flex items-center gap-2.5">
          <div className="p-2 rounded-xl bg-rose-50 text-rose-600 border border-rose-200">
            <Lock className="h-5 w-5" />
          </div>
          <div>
            <h3 className="text-sm font-bold text-slate-900">Change ONU Web Admin Password</h3>
            <p className="text-xs text-slate-500">Update local ONU web interface admin login credentials</p>
          </div>
        </div>
        <button type="button" onClick={onClose} className="p-1 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors">
          <X className="h-4 w-4" />
        </button>
      </div>

      <div className="p-6 space-y-4">
        <div>
          <label className="block text-xs font-semibold text-slate-700 mb-1">Admin Username</label>
          <input
            type="text"
            required
            value={webUser}
            onChange={(e) => setWebUser(e.target.value)}
            className="w-full h-9 border border-slate-200 rounded-xl px-3 text-xs font-medium text-slate-800 bg-white placeholder-slate-400 shadow-xs focus:ring-2 focus:ring-rose-500/20 focus:border-rose-500"
          />
        </div>
        <div>
          <label className="block text-xs font-semibold text-slate-700 mb-1">New Admin Password</label>
          <div className="relative">
            <input
              type={showPass ? "text" : "password"}
              required
              minLength={6}
              value={webPass}
              onChange={(e) => setWebPass(e.target.value)}
              placeholder="Minimum 6 characters"
              className="w-full h-9 border border-slate-200 rounded-xl pl-3 pr-9 text-xs font-mono text-slate-800 bg-white placeholder-slate-400 shadow-xs focus:ring-2 focus:ring-rose-500/20 focus:border-rose-500"
            />
            <button
              type="button"
              onClick={() => setShowPass(!showPass)}
              className="absolute inset-y-0 right-0 pr-3 flex items-center text-slate-400 hover:text-slate-600"
            >
              {showPass ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
            </button>
          </div>
        </div>
      </div>

      <div className="px-6 py-3.5 border-t border-slate-200 bg-slate-50 flex justify-end gap-2">
        <button type="button" onClick={onClose} disabled={busy} className="px-4 py-2 text-xs font-medium text-slate-700 rounded-xl hover:bg-slate-100 border border-slate-200 bg-white shadow-xs transition-all">
          Cancel
        </button>
        <button
          type="submit"
          disabled={busy || !webUser || webPass.length < 6}
          className="px-4 py-2 bg-rose-600 hover:bg-rose-700 text-white text-xs font-bold rounded-xl shadow-xs transition-all disabled:opacity-50 flex items-center gap-1.5"
        >
          {busy && <RefreshCw className="h-3.5 w-3.5 animate-spin" />}
          Update Admin Credentials
        </button>
      </div>
    </form>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// 7. LAN Ethernet Port Modal
// ─────────────────────────────────────────────────────────────────────────────
function LanPortModalInner({
  context,
  topology,
  onClose,
  onSubmit,
  busy,
}: {
  context: ModalContextData;
  topology?: SmartOLTTopology | null;
  onClose: () => void;
  onSubmit: (op: string, payload: Record<string, any>) => Promise<any>;
  busy: boolean;
}) {
  const [port, setPort] = useState("1");
  const [mode, setMode] = useState<"lan" | "access" | "shutdown">("lan");
  const [vlan, setVlan] = useState("");
  const [dhcp, setDhcp] = useState("enabled");

  const vlans = topology?.vlans || [];

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (mode === "shutdown") {
      onSubmit("shutdown_onu_ethernet_port_by_external_id", {
        form_data: { ethernet_port: port },
      });
    } else if (mode === "access") {
      onSubmit("set_onu_ethernet_port_mode_access_by_external_id", {
        form_data: { ethernet_port: port, vlan: vlan, dhcp: dhcp },
      });
    } else {
      onSubmit("set_onu_ethernet_port_mode_lan_by_external_id", {
        form_data: { ethernet_port: port, dhcp: dhcp },
      });
    }
  };

  return (
    <form onSubmit={handleSubmit} className="flex flex-col h-full">
      <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 bg-slate-50">
        <div className="flex items-center gap-2.5">
          <div className="p-2 rounded-xl bg-indigo-50 text-indigo-600 border border-indigo-200">
            <Cable className="h-5 w-5" />
          </div>
          <div>
            <h3 className="text-sm font-bold text-slate-900">Ethernet LAN Port Policy</h3>
            <p className="text-xs text-slate-500">Configure physical Ethernet ports and VLAN bindings</p>
          </div>
        </div>
        <button type="button" onClick={onClose} className="p-1 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors">
          <X className="h-4 w-4" />
        </button>
      </div>

      <div className="p-6 space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1">Select Ethernet Port</label>
            <select
              value={port}
              onChange={(e) => setPort(e.target.value)}
              className="w-full h-9 border border-slate-200 rounded-xl px-3 text-xs font-medium text-slate-800 bg-white shadow-xs focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500"
            >
              <option value="1">Port 1 (GE / FE)</option>
              <option value="2">Port 2</option>
              <option value="3">Port 3</option>
              <option value="4">Port 4</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1">Port Mode</label>
            <select
              value={mode}
              onChange={(e) => setMode(e.target.value as any)}
              className="w-full h-9 border border-slate-200 rounded-xl px-3 text-xs font-medium text-slate-800 bg-white shadow-xs focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500"
            >
              <option value="lan">Standard LAN (Bridged)</option>
              <option value="access">Access (Dedicated VLAN)</option>
              <option value="shutdown">Administrative Shutdown</option>
            </select>
          </div>
        </div>

        {mode === "access" && (
          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1">Assign VLAN</label>
            <select
              required
              value={vlan}
              onChange={(e) => setVlan(e.target.value)}
              className="w-full h-9 border border-slate-200 rounded-xl px-3 text-xs font-medium text-slate-800 bg-white shadow-xs focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500"
            >
              <option value="" className="text-slate-400">Select VLAN ID...</option>
              {vlans.map((v) => (
                <option key={v.vlan_id} value={v.vlan_id}>
                  VLAN {v.vlan_id} ({v.name || "Default"})
                </option>
              ))}
            </select>
          </div>
        )}

        {mode !== "shutdown" && (
          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1">DHCP Server on Port</label>
            <select
              value={dhcp}
              onChange={(e) => setDhcp(e.target.value)}
              className="w-full h-9 border border-slate-200 rounded-xl px-3 text-xs font-medium text-slate-800 bg-white shadow-xs focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500"
            >
              <option value="enabled">Enabled</option>
              <option value="disabled">Disabled</option>
            </select>
          </div>
        )}

        {mode === "shutdown" && (
          <div className="p-3 rounded-xl bg-amber-50 border border-amber-200 text-xs text-amber-800">
            ⚠️ This will physically disable link carrier on Ethernet Port {port}.
          </div>
        )}
      </div>

      <div className="px-6 py-3.5 border-t border-slate-200 bg-slate-50 flex justify-end gap-2">
        <button type="button" onClick={onClose} disabled={busy} className="px-4 py-2 text-xs font-medium text-slate-700 rounded-xl hover:bg-slate-100 border border-slate-200 bg-white shadow-xs transition-all">
          Cancel
        </button>
        <button
          type="submit"
          disabled={busy || (mode === "access" && !vlan)}
          className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold rounded-xl shadow-xs transition-all disabled:opacity-50 flex items-center gap-1.5"
        >
          {busy && <RefreshCw className="h-3.5 w-3.5 animate-spin" />}
          Apply Port Configuration
        </button>
      </div>
    </form>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// 8. Destructive Confirmation Modal
// ─────────────────────────────────────────────────────────────────────────────
function DestructiveConfirmModalInner({
  context,
  onClose,
  onSubmit,
  busy,
}: {
  context: ModalContextData;
  onClose: () => void;
  onSubmit: (op: string, payload: Record<string, any>) => Promise<any>;
  busy: boolean;
}) {
  const dest = context.destructiveAction;
  const [typedInput, setTypedInput] = useState("");

  if (!dest) return null;

  const isConfirmed = typedInput.trim().toUpperCase() === dest.confirmKeyword.toUpperCase();

  const handleConfirm = () => {
    if (!isConfirmed) return;
    onSubmit(dest.key, {});
  };

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center justify-between px-6 py-4 border-b border-rose-200 bg-rose-50">
        <div className="flex items-center gap-2.5">
          <div className="p-2 rounded-xl bg-rose-100 text-rose-600 border border-rose-200">
            <AlertTriangle className="h-5 w-5" />
          </div>
          <div>
            <h3 className="text-sm font-bold text-rose-900">{dest.title}</h3>
            <p className="text-xs text-rose-700">High-Impact Destructive Action</p>
          </div>
        </div>
        <button type="button" onClick={onClose} className="p-1 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors">
          <X className="h-4 w-4" />
        </button>
      </div>

      <div className="p-6 space-y-4">
        <p className="text-xs text-slate-700 leading-relaxed">{dest.description}</p>

        <div className="p-3.5 rounded-xl bg-rose-50/50 border border-rose-200">
          <label className="block text-[11px] font-bold text-rose-900 mb-1.5">
            To proceed, type <span className="font-mono text-rose-700 font-extrabold">{dest.confirmKeyword}</span> below:
          </label>
          <input
            type="text"
            value={typedInput}
            onChange={(e) => setTypedInput(e.target.value)}
            placeholder={`Type ${dest.confirmKeyword} to confirm`}
            className="w-full h-10 border border-rose-300 rounded-xl px-3 text-xs font-mono font-bold text-rose-900 uppercase bg-white placeholder-rose-300 shadow-xs focus:ring-2 focus:ring-rose-500/20 focus:border-rose-500"
          />
        </div>
      </div>

      <div className="px-6 py-3.5 border-t border-slate-200 bg-slate-50 flex justify-end gap-2">
        <button type="button" onClick={onClose} disabled={busy} className="px-4 py-2 text-xs font-medium text-slate-700 rounded-xl hover:bg-slate-100 border border-slate-200 bg-white shadow-xs transition-all">
          Cancel
        </button>
        <button
          type="button"
          onClick={handleConfirm}
          disabled={busy || !isConfirmed}
          className="px-4 py-2 bg-rose-600 text-white text-xs font-bold rounded-xl hover:bg-rose-700 shadow-xs transition-all disabled:opacity-40 flex items-center gap-1.5"
        >
          {busy && <RefreshCw className="h-3.5 w-3.5 animate-spin" />}
          {dest.buttonLabel}
        </button>
      </div>
    </div>
  );
}
